import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { 
  Database, 
  Server, 
  CheckCircle, 
  XCircle, 
  Loader, 
  Play, 
  ArrowRight, 
  ArrowLeft,
  Settings as SettingsIcon,
  FileCode
} from 'lucide-react';
import { notificationService } from '../utils/notifications';

function SetupWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [envVars, setEnvVars] = useState({
    app_url: '',
    db_connection: 'mysql',
    db_host: '127.0.0.1',
    db_port: '3306',
    db_database: '',
    db_username: 'root',
    db_password: '',
    cache_driver: 'file',
    session_driver: 'file',
    session_lifetime: 120,
  });

  // Database config për migrations/seeds
  const [dbConfig, setDbConfig] = useState({
    connection: 'mysql',
    host: '127.0.0.1',
    port: '3306',
    database: '',
    username: 'root',
    password: '',
  });

  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [migrations, setMigrations] = useState([]);
  const [executedMigrations, setExecutedMigrations] = useState([]);
  const [seeders, setSeeders] = useState([]);
  const [selectedSeeders, setSelectedSeeders] = useState([]);
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState('');
  const [freshMigration, setFreshMigration] = useState(false);

  // Sync dbConfig me envVars
  useEffect(() => {
    setDbConfig({
      connection: envVars.db_connection || 'mysql',
      host: envVars.db_host || '127.0.0.1',
      port: envVars.db_port || '3306',
      database: envVars.db_database || '',
      username: envVars.db_username || 'root',
      password: envVars.db_password || '',
    });
  }, [envVars]);

  // Load migrations dhe seeders kur hapi 2 aktivizohet
  useEffect(() => {
    if (currentStep === 2) {
      loadMigrations();
      loadSeeders();
    }
  }, [currentStep]);

  const loadMigrations = async () => {
    try {
      const data = await apiService.get('/database/migrations-setup');
      setMigrations(data.migrations || []);
      setExecutedMigrations(data.executed || []);
    } catch (error) {
      console.error('Error loading migrations:', error);
      // Nëse nuk ka tabela, migrations mund të mos ekzistojnë
      setMigrations([]);
      setExecutedMigrations([]);
    }
  };

  const loadSeeders = async () => {
    try {
      const data = await apiService.get('/database/seeders-setup');
      const seedersList = data.seeders || [];
      setSeeders(seedersList);
      
      // Në setup mode, automatikisht zgjedh PrincipalUserSeeder
      if (seedersList.length > 0 && seedersList[0].class === 'Database\\Seeders\\PrincipalUserSeeder') {
        setSelectedSeeders([seedersList[0].class]);
      }
    } catch (error) {
      console.error('Error loading seeders:', error);
      setSeeders([]);
    }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const response = await apiService.post('/database/test-connection-setup', {
        db_config: dbConfig,
      });
      setConnectionStatus({ success: true, message: response.message });
      notificationService.success('Lidhja me bazën e të dhënave u testua me sukses!');
    } catch (error) {
      setConnectionStatus({ 
        success: false, 
        message: error.response?.data?.error || error.message || 'Gabim gjatë lidhjes me bazën e të dhënave'
      });
      notificationService.error('Gabim gjatë lidhjes: ' + (error.response?.data?.error || error.message));
    } finally {
      setTestingConnection(false);
    }
  };

  // Kontrollo nëse setup është kompletuar dhe redirect në login
  const checkSetupComplete = async () => {
    try {
      const response = await apiService.get('/check-setup');
      if (!response.is_setup_needed) {
        // Setup është kompletuar, redirect në login
        notificationService.success('Setup u kompletuar me sukses! Tani mund të kyçeni.');
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
    } catch (error) {
      console.error('Error checking setup status:', error);
    }
  };

  const runMigrations = async () => {
    setRunning(true);
    setOutput('');
    try {
      const response = await apiService.post('/database/migrate-setup', {
        fresh: freshMigration,
        force: true,
        db_config: dbConfig,
      });
      setOutput(response.output || response.message);
      notificationService.success('Migracionet u ekzekutuan me sukses!');
      loadMigrations();
      // Kontrollo nëse setup është kompletuar
      await checkSetupComplete();
    } catch (error) {
      setOutput(error.response?.data?.error || error.message || 'Gabim gjatë ekzekutimit të migracioneve');
      notificationService.error('Gabim: ' + (error.response?.data?.error || error.message));
    } finally {
      setRunning(false);
    }
  };

  const runSeeders = async () => {
    setRunning(true);
    setOutput('');
    try {
      const response = await apiService.post('/database/seed-setup', {
        seeders: selectedSeeders,
        db_config: dbConfig,
      });
      setOutput(response.output || response.message);
      notificationService.success('Seeds u ekzekutuan me sukses!');
      // Kontrollo nëse setup është kompletuar
      await checkSetupComplete();
    } catch (error) {
      setOutput(error.response?.data?.error || error.message || 'Gabim gjatë ekzekutimit të seeds');
      notificationService.error('Gabim: ' + (error.response?.data?.error || error.message));
    } finally {
      setRunning(false);
    }
  };

  const runMigrationsAndSeeders = async () => {
    setRunning(true);
    setOutput('');
    try {
      const response = await apiService.post('/database/migrate-and-seed-setup', {
        fresh: freshMigration,
        seeders: selectedSeeders,
        db_config: dbConfig,
      });
      setOutput(
        (response.migration_output || '') + 
        '\n\n' + 
        (response.seeder_output || '') + 
        '\n\n' + 
        response.message
      );
      notificationService.success('Migracionet dhe seeds u ekzekutuan me sukses!');
      loadMigrations();
      // Kontrollo nëse setup është kompletuar
      await checkSetupComplete();
    } catch (error) {
      setOutput(error.response?.data?.error || error.message || 'Gabim gjatë ekzekutimit');
      notificationService.error('Gabim: ' + (error.response?.data?.error || error.message));
    } finally {
      setRunning(false);
    }
  };

  const handleSaveEnvVars = async () => {
    try {
      // Ruaj .env variables në backend (përdor endpoint për setup mode)
      await apiService.put('/settings-setup', {
        app_url: envVars.app_url,
        db_connection: envVars.db_connection,
        db_host: envVars.db_host,
        db_port: envVars.db_port,
        db_database: envVars.db_database,
        db_username: envVars.db_username,
        db_password: envVars.db_password,
        cache_driver: envVars.cache_driver,
        session_driver: envVars.session_driver,
        session_lifetime: envVars.session_lifetime,
      });
      notificationService.success('Variablat e .env u ruajtën me sukses!');
      // Kaloj në hapin 2
      setCurrentStep(2);
    } catch (error) {
      notificationService.error('Gabim gjatë ruajtjes: ' + (error.message || 'Gabim i panjohur'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-dark-900 dark:to-dark-800 p-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="card-premium-hover p-6 mb-6 animate-fade-in">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg">
              <SettingsIcon size={28} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200">
                Setup i Sistemit
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Konfiguroni sistemin për përdorim të parë
              </p>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="card-premium-hover p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className={`flex items-center gap-3 ${currentStep >= 1 ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                currentStep >= 1 
                  ? 'bg-primary-500 text-white' 
                  : 'bg-gray-200 dark:bg-dark-700 text-gray-500'
              }`}>
                1
              </div>
              <div>
                <div className="font-semibold">Konfigurim .env Variables</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Vendosni variablat e .env file-it</div>
              </div>
            </div>
            <div className={`flex-1 h-1 mx-4 ${
              currentStep >= 2 ? 'bg-primary-500' : 'bg-gray-200 dark:bg-dark-700'
            }`} />
            <div className={`flex items-center gap-3 ${currentStep >= 2 ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                currentStep >= 2 
                  ? 'bg-primary-500 text-white' 
                  : 'bg-gray-200 dark:bg-dark-700 text-gray-500'
              }`}>
                2
              </div>
              <div>
                <div className="font-semibold">Migracione & Seeds</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Ekzekutoni migracione dhe seeds</div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 1: .env Variables */}
        {currentStep === 1 && (
          <div className="card-premium-hover p-6 animate-slide-up">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <FileCode size={24} className="text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">
                Hapi 1: Konfigurim .env Variables
              </h2>
            </div>

            <div className="space-y-5">
              <div>
                <label className="form-label">APP_URL</label>
                <input
                  type="text"
                  value={envVars.app_url}
                  onChange={(e) => setEnvVars({ ...envVars, app_url: e.target.value })}
                  className="input-premium"
                  placeholder="http://localhost/shop/public"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">DB_CONNECTION</label>
                  <input
                    type="text"
                    value={envVars.db_connection}
                    onChange={(e) => setEnvVars({ ...envVars, db_connection: e.target.value })}
                    className="input-premium"
                    placeholder="mysql"
                  />
                </div>
                <div>
                  <label className="form-label">DB_HOST</label>
                  <input
                    type="text"
                    value={envVars.db_host}
                    onChange={(e) => setEnvVars({ ...envVars, db_host: e.target.value })}
                    className="input-premium"
                    placeholder="127.0.0.1"
                  />
                </div>
                <div>
                  <label className="form-label">DB_PORT</label>
                  <input
                    type="text"
                    value={envVars.db_port}
                    onChange={(e) => setEnvVars({ ...envVars, db_port: e.target.value })}
                    className="input-premium"
                    placeholder="3306"
                  />
                </div>
                <div>
                  <label className="form-label">DB_DATABASE</label>
                  <input
                    type="text"
                    value={envVars.db_database}
                    onChange={(e) => setEnvVars({ ...envVars, db_database: e.target.value })}
                    className="input-premium"
                    placeholder="hmz"
                  />
                </div>
                <div>
                  <label className="form-label">DB_USERNAME</label>
                  <input
                    type="text"
                    value={envVars.db_username}
                    onChange={(e) => setEnvVars({ ...envVars, db_username: e.target.value })}
                    className="input-premium"
                    placeholder="root"
                  />
                </div>
                <div>
                  <label className="form-label">DB_PASSWORD</label>
                  <input
                    type="password"
                    value={envVars.db_password}
                    onChange={(e) => setEnvVars({ ...envVars, db_password: e.target.value })}
                    className="input-premium"
                    placeholder=""
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">CACHE_DRIVER</label>
                  <input
                    type="text"
                    value={envVars.cache_driver}
                    onChange={(e) => setEnvVars({ ...envVars, cache_driver: e.target.value })}
                    className="input-premium"
                    placeholder="file"
                  />
                </div>
                <div>
                  <label className="form-label">SESSION_DRIVER</label>
                  <input
                    type="text"
                    value={envVars.session_driver}
                    onChange={(e) => setEnvVars({ ...envVars, session_driver: e.target.value })}
                    className="input-premium"
                    placeholder="file"
                  />
                </div>
                <div>
                  <label className="form-label">SESSION_LIFETIME</label>
                  <input
                    type="number"
                    value={envVars.session_lifetime}
                    onChange={(e) => setEnvVars({ ...envVars, session_lifetime: parseInt(e.target.value) || 120 })}
                    className="input-premium"
                    placeholder="120"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSaveEnvVars}
                  className="btn-primary flex items-center gap-2"
                >
                  Ruaj dhe Vazhdo
                  <ArrowRight size={18} />
                </button>
                <button
                  onClick={testConnection}
                  disabled={testingConnection}
                  className="btn-secondary flex items-center gap-2"
                >
                  {testingConnection ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      Po teston...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Testo Lidhjen
                    </>
                  )}
                </button>
              </div>

              {connectionStatus && (
                <div className={`p-4 rounded-lg flex items-center gap-2 ${
                  connectionStatus.success
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                }`}>
                  {connectionStatus.success ? (
                    <CheckCircle size={20} />
                  ) : (
                    <XCircle size={20} />
                  )}
                  <span>{connectionStatus.message}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Migrations & Seeds */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="card-premium-hover p-6 animate-slide-up">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Database size={24} className="text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">
                  Hapi 2: Migracione & Seeds
                </h2>
              </div>

              {/* DB Configuration */}
              <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700 mb-6">
                <h3 className="text-lg font-semibold mb-4">Konfigurim i Bazës së të Dhënave</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Host</label>
                    <input
                      type="text"
                      value={dbConfig.host}
                      onChange={(e) => setDbConfig({ ...dbConfig, host: e.target.value })}
                      className="input-premium"
                      placeholder="127.0.0.1"
                    />
                  </div>
                  <div>
                    <label className="form-label">Port</label>
                    <input
                      type="text"
                      value={dbConfig.port}
                      onChange={(e) => setDbConfig({ ...dbConfig, port: e.target.value })}
                      className="input-premium"
                      placeholder="3306"
                    />
                  </div>
                  <div>
                    <label className="form-label">Database</label>
                    <input
                      type="text"
                      value={dbConfig.database}
                      onChange={(e) => setDbConfig({ ...dbConfig, database: e.target.value })}
                      className="input-premium"
                      placeholder="hmz"
                    />
                  </div>
                  <div>
                    <label className="form-label">Username</label>
                    <input
                      type="text"
                      value={dbConfig.username}
                      onChange={(e) => setDbConfig({ ...dbConfig, username: e.target.value })}
                      className="input-premium"
                      placeholder="root"
                    />
                  </div>
                  <div>
                    <label className="form-label">Password</label>
                    <input
                      type="password"
                      value={dbConfig.password}
                      onChange={(e) => setDbConfig({ ...dbConfig, password: e.target.value })}
                      className="input-premium"
                      placeholder=""
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={testConnection}
                      disabled={testingConnection}
                      className="btn-primary w-full flex items-center justify-center gap-2"
                    >
                      {testingConnection ? (
                        <>
                          <Loader size={16} className="animate-spin" />
                          Po teston...
                        </>
                      ) : (
                        <>
                          <CheckCircle size={16} />
                          Testo Lidhjen
                        </>
                      )}
                    </button>
                  </div>
                </div>
                {connectionStatus && (
                  <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
                    connectionStatus.success
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                  }`}>
                    {connectionStatus.success ? (
                      <CheckCircle size={20} />
                    ) : (
                      <XCircle size={20} />
                    )}
                    <span>{connectionStatus.message}</span>
                  </div>
                )}
              </div>

              {/* Migrations */}
              <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700 mb-6">
                <h3 className="text-lg font-semibold mb-4">Migracione</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="fresh_migration"
                      checked={freshMigration}
                      onChange={(e) => setFreshMigration(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 dark:border-dark-600 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="fresh_migration" className="form-label cursor-pointer">
                      Fresh Migration (fshin të gjitha tabelat dhe i krijon përsëri)
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={runMigrations}
                      disabled={running}
                      className="btn-primary flex items-center gap-2"
                    >
                      {running ? (
                        <>
                          <Loader size={16} className="animate-spin" />
                          Po ekzekuton...
                        </>
                      ) : (
                        <>
                          <Play size={16} />
                          Ekzekuto Migracione
                        </>
                      )}
                    </button>
                  </div>
                  {migrations.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Migracionet e disponueshme: {migrations.length}
                      </p>
                      <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-dark-700 rounded-lg p-3 bg-white dark:bg-dark-900">
                        {migrations.map((migration, index) => {
                          const isExecuted = executedMigrations.includes(migration.name) || 
                                            executedMigrations.includes(migration.filename?.replace('.php', ''));
                          return (
                            <div key={index} className={`flex items-center gap-2 py-2 border-b border-gray-100 dark:border-dark-700 last:border-0 ${
                              isExecuted ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              {isExecuted ? (
                                <CheckCircle size={16} className="flex-shrink-0" />
                              ) : (
                                <div className="w-4 h-4 flex-shrink-0" />
                              )}
                              <span className="text-sm font-mono">{migration.name || migration.filename}</span>
                            </div>
                          );
                        })}
                      </div>
                      {executedMigrations.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Ekzekutuar: {executedMigrations.length} / {migrations.length}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Seeders */}
              <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700 mb-6">
                <h3 className="text-lg font-semibold mb-4">Seeds</h3>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Në setup mode, vetëm <strong>PrincipalUserSeeder</strong> duhet të ekzekutohet për të krijuar përdoruesin kryesor.
                  </p>
                  <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-dark-700 rounded-lg p-3 bg-white dark:bg-dark-900">
                    {seeders.length > 0 ? (
                      seeders.map((seeder) => (
                        <div key={seeder.name} className="flex items-center gap-2 py-3 px-2 bg-primary-50 dark:bg-primary-900/20 rounded border border-primary-200 dark:border-primary-800">
                          <input
                            type="checkbox"
                            id={`seeder_${seeder.name}`}
                            checked={selectedSeeders.includes(seeder.class)}
                            disabled
                            className="w-4 h-4 rounded border-gray-300 dark:border-dark-600 text-primary-600 focus:ring-primary-500 cursor-not-allowed opacity-70"
                          />
                          <label htmlFor={`seeder_${seeder.name}`} className="text-sm font-semibold text-primary-700 dark:text-primary-300 font-mono">
                            {seeder.name}
                          </label>
                          <span className="ml-auto text-xs text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/40 px-2 py-1 rounded">
                            (Obligativ)
                          </span>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                        Nuk ka seeds të disponueshëm
                      </p>
                    )}
                  </div>
                  {seeders.length > 0 && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        <strong>Shënim:</strong> PrincipalUserSeeder do të ekzekutohet automatikisht kur klikoni "Ekzekuto Migracione + Seeds". 
                        Për seeders të tjerë, mund t'i ekzekutoni pas login nga Settings.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Run Both */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mb-6">
                <h3 className="text-lg font-semibold mb-4">Ekzekuto Migracione + Seeds</h3>
                <button
                  onClick={runMigrationsAndSeeders}
                  disabled={running}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  {running ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      Po ekzekuton...
                    </>
                  ) : (
                    <>
                      <Play size={16} />
                      Ekzekuto Migracione dhe Seeds
                    </>
                  )}
                </button>
              </div>

              {/* Output */}
              {output && (
                <div className="p-4 bg-gray-900 dark:bg-dark-900 rounded-lg border border-gray-700 dark:border-dark-600">
                  <h3 className="text-lg font-semibold mb-2 text-white">Output</h3>
                  <pre className="text-sm text-green-400 font-mono overflow-auto max-h-60 whitespace-pre-wrap">
                    {output}
                  </pre>
                </div>
              )}

              {/* Navigation */}
              <div className="flex gap-3 pt-4 mt-6 border-t border-gray-200 dark:border-dark-700">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="btn-secondary flex items-center gap-2"
                >
                  <ArrowLeft size={18} />
                  Kthehu te Hapi 1
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SetupWizard;

